import { useState, useCallback, useEffect, useMemo, useRef } from "react";

interface UseFilterDraftOptions<T> {
  /**
   * Factory function to create an empty value.
   * Required for object drafts (e.g., { ids: [], cache: [] })
   */
  getEmptyValue?: () => T;

  /**
   * Custom equality comparison function.
   * Used to detect changes and prevent unnecessary useEffect triggers.
   */
  isEqual?: (a: T, b: T) => boolean;
}

const defaultIsEqual = <T>(a: T, b: T) => a === b;

export function useFilterDraft<T>(
  initialValue: T,
  onApply: (value: T) => void,
  options?: UseFilterDraftOptions<T>
) {
  const [draft, setDraft] = useState<T>(initialValue);
  const [hasChanges, setHasChanges] = useState(false);

  // Memoize equality check to prevent dependency changes on every render
  const isEqual = useMemo(
    () => options?.isEqual || defaultIsEqual<T>,
    [options?.isEqual]
  );

  // Track previous initialValue to detect external changes
  const prevInitialValueRef = useRef<T>(initialValue);

  // Only reset draft when initialValue changes externally (not when draft changes)
  useEffect(() => {
    if (!isEqual(prevInitialValueRef.current, initialValue)) {
      setDraft(initialValue);
      setHasChanges(false);
      prevInitialValueRef.current = initialValue;
    }
  }, [initialValue, isEqual]);

  const updateDraft = useCallback(
    (newValue: T) => {
      setDraft(newValue);
      setHasChanges(!isEqual(newValue, initialValue));
    },
    [initialValue, isEqual]
  );

  const applyChanges = useCallback(() => {
    onApply(draft);
    setHasChanges(false);
  }, [draft, onApply]);

  const cancelChanges = useCallback(() => {
    setDraft(initialValue);
    setHasChanges(false);
  }, [initialValue]);

  const clearDraft = useCallback(() => {
    const emptyValue = options?.getEmptyValue
      ? options.getEmptyValue()
      : Array.isArray(initialValue)
        ? ([] as unknown as T)
        : initialValue;

    setDraft(emptyValue);
    setHasChanges(!isEqual(emptyValue, initialValue));
  }, [initialValue, options, isEqual]);

  return {
    draft,
    updateDraft,
    applyChanges,
    cancelChanges,
    clearDraft,
    hasChanges,
  };
}
